import { formatUpdated } from "./taxSources";

/**
 * US sales tax: state base rate and approximate average combined (state + local) rate, in percent, all 50 states + DC.
 * Source: Tax Foundation "State and Local Sales Tax Rates, Midyear 2026" (state rates and combined rates as of 1 July 2026;
 * the average local column is as of 1 January 2026). On 30 Oct 2026 (B-0126) all 51 rows were compared with that table, fetched twice
 * from the Tax Foundation 2026 pages; every combined rate matched (e.g. LA 10.13, WA 9.57, WY 5.39, NC 7.10) and the base rates
 * matched to the two decimals the table prints (MN 6.875, MO 4.225, NM 4.875, NJ 6.625 are kept to three). The brief's own figures
 * (LA 10.11, WA 9.51, WY 5.56, NC 7.00) are not in that table, so no preset was changed. Re-check each January and July.
 */
export const SALES_TAX_AS_OF = "Tax Foundation, 1 July 2026";
/** The table's own date (ISO) and the day all 51 rows were last compared with it. */
export const SALES_TAX_TABLE_DATE = "2026-07-01";
export const SALES_TAX_CHECKED_ON = "2026-10-30";
/**
 * The Tax Foundation publishes in January and July. A table older than this many days has very likely been replaced
 * (July 1 + 200 days is 17 January), so the page then says the rates may be out of date.
 */
export const SALES_TAX_STALE_AFTER_DAYS = 200;
export const SALES_TAX_SOURCE_URL = "https://taxfoundation.org/data/all/state/sales-tax-rates/";

export const SALES_TAX: { state: string; base: number; combined: number }[] = [
  ["Alabama", 4, 9.46], ["Alaska", 0, 1.82], ["Arizona", 5.6, 8.54], ["Arkansas", 6.5, 9.48], ["California", 7.25, 9.03],
  ["Colorado", 2.9, 7.89], ["Connecticut", 6.35, 6.35], ["Delaware", 0, 0], ["District of Columbia", 6, 6], ["Florida", 6, 6.98],
  ["Georgia", 4, 7.56], ["Hawaii", 4, 4.5], ["Idaho", 6, 6.03], ["Illinois", 6.25, 8.98], ["Indiana", 7, 7],
  ["Iowa", 6, 6.94], ["Kansas", 6.5, 8.71], ["Kentucky", 6, 6], ["Louisiana", 5, 10.13], ["Maine", 5.5, 5.5],
  ["Maryland", 6, 6], ["Massachusetts", 6.25, 6.25], ["Michigan", 6, 6], ["Minnesota", 6.875, 8.14], ["Mississippi", 7, 7.06],
  ["Missouri", 4.225, 8.44], ["Montana", 0, 0], ["Nebraska", 5.5, 6.98], ["Nevada", 6.85, 8.24], ["New Hampshire", 0, 0],
  ["New Jersey", 6.625, 6.6], ["New Mexico", 4.875, 7.68], ["New York", 4, 8.54], ["North Carolina", 4.75, 7.1],
  ["North Dakota", 5, 7.09], ["Ohio", 5.75, 7.29], ["Oklahoma", 4.5, 9.06], ["Oregon", 0, 0], ["Pennsylvania", 6, 6.34],
  ["Rhode Island", 7, 7], ["South Carolina", 6, 7.49], ["South Dakota", 4.2, 6.11], ["Tennessee", 7, 9.61], ["Texas", 6.25, 8.2],
  ["Utah", 6.1, 7.42], ["Vermont", 6, 6.43], ["Virginia", 5.3, 5.77], ["Washington", 6.5, 9.57], ["West Virginia", 6, 6.6],
  ["Wisconsin", 5, 5.72], ["Wyoming", 4, 5.39],
].map(([state, base, combined]) => ({ state: state as string, base: base as number, combined: combined as number }));

/** Every state in the table was compared with the 1 July 2026 release on 30 Oct 2026. */
export const SALES_TAX_JULY_2026: string[] = SALES_TAX.map((r) => r.state);

export interface SalesTaxVintage { tableDate: string; tableDateLabel: string; checkedOn: string; checkedOnLabel: string; ageDays: number; stale: boolean; text: string; warning: string | null }

const DAY = 86_400_000;
/** How current the table is on `now`: its date, the day it was checked, its age in days and, when old, a warning. */
export function salesTaxVintage(now: Date = new Date()): SalesTaxVintage {
  const ageDays = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - Date.parse(SALES_TAX_TABLE_DATE)) / DAY);
  const stale = ageDays > SALES_TAX_STALE_AFTER_DAYS;
  const tableDateLabel = formatUpdated(SALES_TAX_TABLE_DATE), checkedOnLabel = formatUpdated(SALES_TAX_CHECKED_ON);
  return {
    tableDate: SALES_TAX_TABLE_DATE, tableDateLabel, checkedOn: SALES_TAX_CHECKED_ON, checkedOnLabel, ageDays, stale,
    text: `Rates as of ${tableDateLabel} (${SALES_TAX_AS_OF.split(",")[0]} midyear 2026 table), compared with that table on ${checkedOnLabel}.`,
    warning: stale ? `These rates are from the ${tableDateLabel} table, which is ${ageDays} days old. The Tax Foundation publishes a new table each January and July, so a newer one probably exists. Check the exact rate for your address before relying on it.` : null,
  };
}

export interface SalesTaxResult { subtotal: number; discount: number; base: number; tax: number; total: number }

/** price per item × quantity, less a % discount, then tax. In inclusive mode the entered amount already contains tax. */
export function salesTaxCalc(price: number, qty: number, discountPct: number, ratePct: number, inclusive: boolean): SalesTaxResult {
  const subtotal = price * qty;
  const discount = (subtotal * Math.min(Math.max(discountPct, 0), 100)) / 100;
  const after = subtotal - discount;
  if (inclusive) { const base = after / (1 + ratePct / 100); return { subtotal, discount, base, tax: after - base, total: after }; }
  const tax = (after * ratePct) / 100;
  return { subtotal, discount, base: after, tax, total: after + tax };
}
